import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiError } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  Send, 
  Package, 
  FileText,
  Download,
  Truck,
  Clock,
  DollarSign,
  User,
  MessageSquare,
  CheckCircle,
  ShieldCheck,
  Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShipmentDetail = () => {
  const { waybillId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const backPath = from || '/admin/operations';
  const backLabel = from === '/admin/accepted-shipments' ? 'Back to Waybills'
    : from === '/admin/dashboard' ? 'Back to Dashboard'
    : from === '/admin/quote-queue' ? 'Back to Quote Queue'
    : from === '/admin/customers' ? 'Back to Customers'
    : from?.startsWith('/admin/customer/') ? 'Back to Billing'
    : 'Back to Operations';
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [waybill, setWaybill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [quoteData, setQuoteData] = useState({
    base_charge: '',
    fuel_surcharge: '',
    customs_fee: '',
    admin_fee: '',
    insurance: '',
    other: ''
  });

  const [statusData, setStatusData] = useState({
    status: '',
    location: '',
    notes: ''
  });

  const [internalNotes, setInternalNotes] = useState('');
  const [itemCosts, setItemCosts] = useState({});

  const fetchWaybill = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/waybills/${waybillId}`);
      const data = response.data;
      
      setWaybill(data);
      setInternalNotes(data.internal_notes || '');
      
      // Load existing line-item costs from quote breakdown (drafts / existing quotes)
      if (data.quote_breakdown?.line_items) {
        const costs = {};
        data.quote_breakdown.line_items.forEach(li => {
          costs[li.item_id] = {
            third_party_cost: li.third_party_cost || 0,
            additional_cost: li.additional_cost || 0,
            markup_percentage: li.markup_percentage || 0
          };
        });
        setItemCosts(costs);
      }
      
      if (response.data.quote_breakdown) {
        setQuoteData({
          base_charge: response.data.quote_breakdown.base_charge || '',
          fuel_surcharge: response.data.quote_breakdown.fuel_surcharge || '',
          customs_fee: response.data.quote_breakdown.customs_fee || '',
          admin_fee: response.data.quote_breakdown.admin_fee || '',
          insurance: response.data.quote_breakdown.insurance || '',
          other: response.data.quote_breakdown.other || ''
        });
      }
    } catch (err) {
      console.error('Error fetching waybill:', err);
      toast.error('Failed to load waybill');
    } finally {
      setLoading(false);
    }
  }, [waybillId]);

  useEffect(() => {
    fetchWaybill();
  }, [fetchWaybill]);

  const handleSendQuote = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/quote`, {
        base_charge: parseFloat(quoteData.base_charge) || 0,
        fuel_surcharge: parseFloat(quoteData.fuel_surcharge) || 0,
        customs_fee: parseFloat(quoteData.customs_fee) || 0,
        admin_fee: parseFloat(quoteData.admin_fee) || 0,
        insurance: parseFloat(quoteData.insurance) || 0,
        other: parseFloat(quoteData.other) || 0
      });
      toast.success('Quote sent to customer');
      setQuoteOpen(false);
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to send quote'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusData.status && !statusData.notes) {
      toast.error('Please select a status or add a note');
      return;
    }
    if (!statusData.location) {
      toast.error('Please fill in the location');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/status`, {
        status: statusData.status || null,
        location: statusData.location,
        notes: statusData.notes
      });
      toast.success(statusData.status ? 'Status updated' : 'Note added to timeline');
      setStatusOpen(false);
      setStatusData({ status: '', location: '', notes: '' });
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/internal-notes`, { notes: internalNotes });
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    }
  };

  const handleAcknowledge = async () => {
    setSubmitting(true);
    try {
      const items = waybill.items?.map(item => {
        const costs = itemCosts[item.item_id] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
        return {
          item_id: item.item_id,
          third_party_cost: parseFloat(costs.third_party_cost) || 0,
          additional_cost: parseFloat(costs.additional_cost) || 0,
          markup_percentage: parseFloat(costs.markup_percentage) || 0
        };
      }) || [];
      await axios.put(`${API_URL}/api/waybills/${waybillId}/acknowledge`, { items });
      toast.success('Waybill acknowledged and accepted');
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to acknowledge waybill'));
    } finally {
      setSubmitting(false);
    }
  };

  const calculateUnitCost = (itemId) => {
    const costs = itemCosts[itemId] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
    const baseCost = (parseFloat(costs.third_party_cost) || 0) + (parseFloat(costs.additional_cost) || 0);
    return baseCost + baseCost * ((parseFloat(costs.markup_percentage) || 0) / 100);
  };

  const calculateLineTotal = (itemId, quantity) => {
    return calculateUnitCost(itemId) * (quantity || 1);
  };

  const calculateTotals = () => {
    let subtotal = 0, markup = 0;
    waybill?.items?.forEach(item => {
      const qty = item.quantity || 1;
      const costs = itemCosts[item.item_id] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
      const baseCost = (parseFloat(costs.third_party_cost) || 0) + (parseFloat(costs.additional_cost) || 0);
      subtotal += baseCost * qty;
      markup += baseCost * ((parseFloat(costs.markup_percentage) || 0) / 100) * qty;
    });
    return { subtotal, markup, total: subtotal + markup };
  };

  const buildLineItems = () => {
    return waybill?.items?.map(item => {
      const costs = itemCosts[item.item_id] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
      return {
        item_id: item.item_id,
        third_party_cost: parseFloat(costs.third_party_cost) || 0,
        additional_cost: parseFloat(costs.additional_cost) || 0,
        markup_percentage: parseFloat(costs.markup_percentage) || 0
      };
    }) || [];
  };

  const handleSubmitQuote = async (isDraft = false, requestApproval = false) => {
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/line-item-quote`, {
        items: buildLineItems(),
        is_draft: isDraft,
        request_approval: requestApproval
      });
      if (isDraft) {
        toast.success('Draft saved');
      } else if (requestApproval) {
        toast.success('Quote sent for approval');
      } else {
        toast.success('Quote sent to client');
      }
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to submit quote'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveQuote = async () => {
    setSubmitting(true);
    try {
      const payload = { items: buildLineItems(), is_draft: false };
      await axios.put(`${API_URL}/api/waybills/${waybillId}/approve-quote`, payload);
      toast.success('Quote approved and sent to client');
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to approve quote'));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPDF = async (type) => {
    try {
      const endpoint = type === 'waybill' ? 'pdf' : 'quote-pdf';
      const response = await axios.get(`${API_URL}/api/waybills/${waybillId}/${endpoint}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${waybill.reference_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `R ${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const calculateTotal = () => {
    return Object.values(quoteData).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const operationalStatuses = [
    { value: 'collected', label: 'Collected' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!waybill) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Waybill not found</p>
        </div>
      </AdminLayout>
    );
  }

  const canSendQuote = ['quote_requested', 'in_costing'].includes(waybill.status);
  const canApproveQuote = waybill.status === 'pending_approval';
  const canAcknowledge = ['confirmed', 'accepted'].includes(waybill.status);
  const canUpdateStatus = ['acknowledged', 'dispatch_scheduled', 'collected', 'in_transit', 'at_customs', 'out_for_delivery', 'delayed'].includes(waybill.status);
  const isShipNow = waybill.submit_type === 'direct';
  const isFromQuote = waybill.submit_type === 'quote';
  const costingLocked = canAcknowledge && isFromQuote && !!waybill.quoted_amount;
  const showCosting = canSendQuote || canAcknowledge || canApproveQuote;

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="shipment-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link to={backPath} className="inline-flex items-center text-sm text-slate-500 hover:text-teal-700 mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {backLabel}
            </Link>
            <div className="flex items-center space-x-4">
              <h1 className="page-title">{waybill.reference_number}</h1>
              <StatusBadge status={waybill.status} />
              {isShipNow ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200" data-testid="ship-now-badge">
                  <Truck className="h-3 w-3" />
                  Ship Now
                </span>
              ) : isFromQuote ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200" data-testid="from-quote-badge">
                  <FileText className="h-3 w-3" />
                  From Quote
                </span>
              ) : null}
            </div>
            <p className="text-slate-600 mt-1">
              {waybill.origin_city} → {waybill.destination_city}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {canUpdateStatus && (
              <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="update-status-btn" onClick={() => setStatusData(prev => ({ ...prev, location: waybill?.origin_city || '' }))}>
                    <Truck className="h-4 w-4 mr-2" />
                    Update Status
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Update Shipment Status</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>New Status <span className="text-xs text-slate-400 font-normal">(optional — leave blank to add note only)</span></Label>
                      <Select value={statusData.status} onValueChange={(v) => setStatusData({ ...statusData, status: v, location: v === 'delivered' ? (waybill?.destination_city || '') : (waybill?.origin_city || '') })}>
                        <SelectTrigger data-testid="status-select-trigger">
                          <SelectValue placeholder="Select status (or skip for note)" />
                        </SelectTrigger>
                        <SelectContent>
                          {operationalStatuses.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={statusData.location}
                        onChange={(e) => setStatusData({ ...statusData, location: e.target.value })}
                        placeholder="e.g., Johannesburg Depot"
                        data-testid="status-location-input"
                      />
                    </div>
                    <div>
                      <Label>Notes {!statusData.status && <span className="text-xs text-amber-600 font-normal">(required when no status selected)</span>}</Label>
                      <Textarea
                        value={statusData.notes}
                        onChange={(e) => setStatusData({ ...statusData, notes: e.target.value })}
                        placeholder="e.g., Road closed delivery delayed..."
                        data-testid="status-notes-input"
                      />
                    </div>
                    <Button onClick={handleUpdateStatus} disabled={submitting} className="w-full btn-primary" data-testid="submit-status-btn">
                      {submitting ? 'Updating...' : statusData.status ? 'Update Status' : 'Add Note to Timeline'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            <Button variant="outline" onClick={() => downloadPDF('waybill')}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quote Summary */}
            {waybill.quoted_amount && (
              <Card className="card-elevated border-l-4 border-l-teal-600" data-testid="quote-summary">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Quoted Amount</p>
                      <p className="text-3xl font-bold text-teal-700">{formatCurrency(waybill.quoted_amount)}</p>
                    </div>
                    {waybill.quote_breakdown && (
                      <div className="text-right text-sm text-slate-500">
                        <p>Base: {formatCurrency(waybill.quote_breakdown.base_charge)}</p>
                        <p>Fuel: {formatCurrency(waybill.quote_breakdown.fuel_surcharge)}</p>
                        <p>Customs: {formatCurrency(waybill.quote_breakdown.customs_fee)}</p>
                        <p>Admin: {formatCurrency(waybill.quote_breakdown.admin_fee)}</p>
                        <p>Insurance: {formatCurrency(waybill.quote_breakdown.insurance)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Customer Info */}
            {waybill.customer && (
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <User className="h-5 w-5 text-indigo-600" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500">Customer</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-lg font-semibold text-indigo-600">
                        {waybill.customer.company_name?.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{waybill.customer.company_name}</p>
                      <p className="text-sm text-slate-500">{waybill.customer.account_code}</p>
                      <p className="text-sm text-slate-500">{waybill.customer.email}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sender & Receiver */}
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-teal-600" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500">Sender</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-slate-900">{waybill.sender_company}</p>
                  <p className="text-sm text-slate-600 mt-1">{waybill.sender_address}</p>
                  <p className="text-sm text-slate-600 mt-2">{waybill.sender_contact_person}</p>
                  <p className="text-sm text-slate-500">{waybill.sender_contact_number}</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <Send className="h-5 w-5 text-purple-600" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500">Receiver</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-slate-900">{waybill.receiver_company}</p>
                  <p className="text-sm text-slate-600 mt-1">{waybill.receiver_address}</p>
                  <p className="text-sm text-slate-600 mt-2">{waybill.receiver_contact_person}</p>
                  <p className="text-sm text-slate-500">{waybill.receiver_contact_number}</p>
                </CardContent>
              </Card>
            </div>

            {/* Delivery Type */}
            {waybill.delivery_type_name && (
              <Card className="card-elevated">
                <CardContent className="p-4 flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Truck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Delivery Type</p>
                    <p className="font-semibold text-slate-900">{waybill.delivery_type_name}</p>
                  </div>
                  {waybill.delivery_expected_date && (
                    <div className="ml-auto text-right">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Expected Delivery</p>
                      <p className="font-semibold text-slate-900">{formatDate(waybill.delivery_expected_date)}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Items */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    <CardTitle className="card-title">Items ({waybill.items?.length || 0})</CardTitle>
                  </div>
                  {showCosting && !costingLocked && (
                    <p className="text-sm text-slate-500">Update line costs and markups to generate freight total</p>
                  )}
                  {costingLocked && (
                    <p className="text-sm text-blue-600">Costs locked — this waybill was accepted from a quote</p>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {showCosting ? (
                  <div className="space-y-6">
                    {waybill.items?.map((item, idx) => {
                      const costs = itemCosts[item.item_id] || { third_party_cost: 0, additional_cost: 0, markup_percentage: 0 };
                      const unitCost = calculateUnitCost(item.item_id);
                      const qty = item.quantity || 1;
                      const lineTotal = unitCost * qty;
                      return (
                        <div key={idx} className="border border-slate-200 rounded-lg p-4" data-testid={`costing-item-${idx}`}>
                          {/* Item info row */}
                          <div className="flex flex-wrap items-center gap-4 mb-4 pb-3 border-b border-slate-100">
                            <div className="flex-1 min-w-[200px]">
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Description</span>
                              <p className="font-medium text-slate-900">{item.description || '-'}</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ref #</span>
                              <p className="text-slate-700">{item.item_reference || '-'}</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Qty</span>
                              <p className="text-slate-700 font-semibold">{qty}</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Weight/item</span>
                              <p className="text-slate-700">{item.weight} kg</p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Dimensions</span>
                              <p className="text-slate-700">{item.length}×{item.width}×{item.height} cm</p>
                            </div>
                            <div className="capitalize">
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Type</span>
                              <p className="text-slate-700">{item.package_type}</p>
                            </div>
                          </div>
                          {/* Costing inputs row */}
                          <div className="grid grid-cols-4 gap-4 items-end">
                            <div>
                              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">3rd Party Cost (R)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={costs.third_party_cost}
                                onChange={(e) => setItemCosts(prev => ({
                                  ...prev,
                                  [item.item_id]: { ...prev[item.item_id], third_party_cost: e.target.value, additional_cost: prev[item.item_id]?.additional_cost || 0, markup_percentage: prev[item.item_id]?.markup_percentage || 0 }
                                }))}
                                placeholder="0.00"
                                disabled={costingLocked}
                                className={costingLocked ? "bg-slate-100 text-slate-600 border-slate-200 h-11 mt-1 cursor-not-allowed" : "bg-slate-800 text-white border-slate-700 h-11 mt-1"}
                                data-testid={`item-3rd-party-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">CargoDash Cost (R)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={costs.additional_cost}
                                onChange={(e) => setItemCosts(prev => ({
                                  ...prev,
                                  [item.item_id]: { ...prev[item.item_id], third_party_cost: prev[item.item_id]?.third_party_cost || 0, additional_cost: e.target.value, markup_percentage: prev[item.item_id]?.markup_percentage || 0 }
                                }))}
                                placeholder="0.00"
                                disabled={costingLocked}
                                className={costingLocked ? "bg-slate-100 text-slate-600 border-slate-200 h-11 mt-1 cursor-not-allowed" : "bg-slate-800 text-white border-slate-700 h-11 mt-1"}
                                data-testid={`item-additional-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Markup (%)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                value={costs.markup_percentage}
                                onChange={(e) => setItemCosts(prev => ({
                                  ...prev,
                                  [item.item_id]: { ...prev[item.item_id], third_party_cost: prev[item.item_id]?.third_party_cost || 0, additional_cost: prev[item.item_id]?.additional_cost || 0, markup_percentage: e.target.value }
                                }))}
                                placeholder="15"
                                disabled={costingLocked}
                                className={costingLocked ? "bg-slate-100 text-slate-600 border-slate-200 h-11 mt-1 cursor-not-allowed" : "bg-slate-800 text-white border-slate-700 h-11 mt-1"}
                                data-testid={`item-markup-${idx}`}
                              />
                            </div>
                            <div className="text-right">
                              {qty > 1 ? (
                                <>
                                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit Cost</span>
                                  <p className="text-base font-semibold text-slate-700 mt-1" data-testid={`item-unit-cost-${idx}`}>
                                    {formatCurrency(unitCost)}
                                  </p>
                                  <div className="mt-1 pt-1 border-t border-slate-100">
                                    <span className="text-xs text-slate-400">× {qty} =</span>
                                    <p className="text-xl font-bold text-teal-700" data-testid={`item-line-total-${idx}`}>
                                      {formatCurrency(lineTotal)}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <p className="text-xl font-bold text-teal-700 mt-1" data-testid={`item-line-total-${idx}`}>
                                  {formatCurrency(lineTotal)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Weight</th>
                          <th>Dimensions</th>
                          <th className="text-right">Quoted Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waybill.items?.map((item, idx) => {
                          const lineItem = waybill.quote_breakdown?.line_items?.find(li => li.item_id === item.item_id);
                          return (
                            <tr key={idx}>
                              <td className="capitalize">{item.package_type}</td>
                              <td>{item.description || '-'}</td>
                              <td>{item.quantity}</td>
                              <td>{item.weight} kg</td>
                              <td>{item.length}×{item.width}×{item.height} cm</td>
                              <td className="text-right font-medium">{lineItem?.line_total ? formatCurrency(lineItem.line_total) : '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tracking Timeline */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  <CardTitle className="card-title">Tracking History</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {waybill.tracking_events?.length > 0 ? (
                  <div className="space-y-4">
                    {waybill.tracking_events.map((event, idx) => (
                      <div key={event.event_id || `te-${idx}`} className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-teal-600' : 'bg-slate-300'}`}></div>
                          {idx < waybill.tracking_events.length - 1 && (
                            <div className="w-0.5 h-full bg-slate-200 my-1"></div>
                          )}
                        </div>
                        <div className="pb-4 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <StatusBadge status={event.status} />
                              {!event.customer_visible && (
                                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Internal</span>
                              )}
                            </div>
                            <span className="text-xs text-slate-500">{formatDateTime(event.timestamp)}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{event.location}</p>
                          {event.notes && <p className="text-sm text-slate-500 mt-1">{event.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No tracking events yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Freight Total + Actions (when costing) */}
            {showCosting && (
              <Card className="card-elevated border-t-4 border-t-teal-600" data-testid="freight-total-card">
                <CardContent className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-teal-600 mb-1">Base Freight Total</p>
                  <p className="text-4xl font-bold text-teal-700 mb-4" data-testid="freight-grand-total">
                    {formatCurrency(calculateTotals().total)}
                  </p>
                  <div className="space-y-2 mb-6 border-t border-slate-100 pt-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Subtotal Cost</span>
                      <span className="text-sm font-medium text-slate-900" data-testid="freight-subtotal">{formatCurrency(calculateTotals().subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-500">Total Markup</span>
                      <span className="text-sm font-medium text-slate-900" data-testid="freight-markup">{formatCurrency(calculateTotals().markup)}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {canAcknowledge && (
                      <Button
                        onClick={handleAcknowledge}
                        disabled={submitting}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base"
                        data-testid="acknowledge-accept-btn"
                      >
                        <CheckCircle className="h-5 w-5 mr-2" />
                        {submitting ? 'Accepting...' : 'Accept Waybill'}
                      </Button>
                    )}
                    {canSendQuote && (
                      <>
                        <Button
                          onClick={() => handleSubmitQuote(false, false)}
                          disabled={submitting}
                          className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base"
                          data-testid="submit-quote-btn"
                        >
                          <Send className="h-5 w-5 mr-2" />
                          {submitting ? 'Sending...' : 'Send Quote to Client'}
                        </Button>
                        {!isSuperAdmin && (
                          <Button
                            onClick={() => handleSubmitQuote(false, true)}
                            disabled={submitting}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-base"
                            data-testid="request-approval-btn"
                          >
                            <ShieldCheck className="h-5 w-5 mr-2" />
                            {submitting ? 'Submitting...' : 'Send for Approval'}
                          </Button>
                        )}
                        <Button
                          onClick={() => handleSubmitQuote(true)}
                          disabled={submitting}
                          variant="outline"
                          className="w-full h-10"
                          data-testid="save-draft-btn"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Draft
                        </Button>
                      </>
                    )}
                    {canApproveQuote && isSuperAdmin && (
                      <Button
                        onClick={handleApproveQuote}
                        disabled={submitting}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base"
                        data-testid="approve-quote-btn"
                      >
                        <ShieldCheck className="h-5 w-5 mr-2" />
                        {submitting ? 'Approving...' : 'Approve & Send to Client'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <CardTitle className="card-title">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Reference</span>
                  <span className="text-sm font-medium text-slate-900">{waybill.reference_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Submitted</span>
                  <span className="text-sm font-medium text-slate-900">{formatDate(waybill.submission_date)}</span>
                </div>
                {waybill.quoted_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Quoted</span>
                    <span className="text-sm font-medium text-slate-900">{formatDate(waybill.quoted_date)}</span>
                  </div>
                )}
                {waybill.accepted_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Accepted</span>
                    <span className="text-sm font-medium text-slate-900">{formatDate(waybill.accepted_date)}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Total Items</span>
                    <span className="text-sm font-medium text-slate-900">{waybill.items?.length || 0}</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-sm text-slate-500">Total Weight</span>
                    <span className="text-sm font-medium text-slate-900">
                      {waybill.items?.reduce((sum, item) => sum + (item.weight * item.quantity), 0).toFixed(2)} kg
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Internal Notes */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                  <CardTitle className="card-title">Internal Notes</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Add internal notes (not visible to customer)..."
                  className="min-h-[100px]"
                />
                <Button onClick={handleSaveNotes} size="sm" className="mt-3 w-full" variant="outline">
                  Save Notes
                </Button>
              </CardContent>
            </Card>

            {/* Customer Notes */}
            {waybill.customer_notes && (
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="card-title">Customer Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">{waybill.customer_notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ShipmentDetail;
